import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { Party } from '../../interfaces/parties/party';
import { PartyForCreation } from '../../interfaces/parties/party-for-creation';
import { ePartyLinkType } from '../../enums';
import { PartyLink } from '../../interfaces/parties/party-link';
import { OrderManagerService } from '../order-manager/order-manager.service';
import { map, tap } from 'rxjs';
import { Validation } from '../../interfaces/validation/validation';
import { PatchDocument } from '../../interfaces/patch-document';

@Injectable({
  providedIn: 'root'
})
export class PartyRepositoryService {

  constructor(private orderManager: OrderManagerService,
    private http: HttpClient,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) { }

  // CRUD Operations ---------------
  public getParty(orderID: string, partyID: string) {
    return this.http.get<Party>(this.createCompleteRoute(`orders/${orderID}/parties/${partyID}`, this.config.rootUrl));
  }

  public createParty = (route: string, party: Party) => {
    return this.http.post<Party>(
      this.createCompleteRoute(route, this.config.rootUrl),
      party,
      this.generateHeaders());
  }

  public createNewParty = (route: string, party: PartyForCreation) => {
    return this.http.post<{ resource: Party, validation: Validation }>(
      this.createCompleteRoute(route, this.config.rootUrl),
      party,
      this.generateHeaders());
  }

  public updateParty = (route: string, party: Party) => {
    return this.http.put(this.createCompleteRoute(route, this.config.rootUrl), party, this.generateHeaders());
  }

  public patchParty(orderID: string, partyID: string, patchDocument: PatchDocument[]) {
    return this.http.patch<{ resource: Party }>(`${this.config.rootUrl}/Orders/${orderID}/Parties/${partyID}`, patchDocument)
      .pipe(map(response => response.resource));
  }

  public deleteParty = (route: string) => {
    return this.http.delete(this.createCompleteRoute(route, this.config.rootUrl));
  }

  public linkParties(parentPartyID: string, childPartyID: string) {
    return this.http.post<PartyLink>(
        this.createCompleteRoute(`Parties/${parentPartyID}/LinkedParties`, this.config.rootUrl),
        { linkedPartyID: childPartyID, partyLinkType: ePartyLinkType.association })
      .pipe(tap(link => {
        const order = this.orderManager.currentOrder.value;
        const partyIndex = order.parties?.findIndex(p => p.id === parentPartyID) ?? -1;

        if(partyIndex !== -1) {
          order.parties?.at(partyIndex)?.linkedParties?.push(link);
          this.orderManager.updateOrder(order);
        }
      }));
  }

  public unlinkParties(parentPartyID: string, linkID: string) {
    return this.http.delete(
        this.createCompleteRoute(`Parties/${parentPartyID}/LinkedParties/${linkID}`, this.config.rootUrl))
      .pipe(tap(() => {
        const order = this.orderManager.currentOrder.value;
        const partyIndex = order.parties?.findIndex(p => p.id === parentPartyID) ?? -1;

        if(partyIndex === -1) {
          return;
        }

        const indexOfLink = order.parties?.at(partyIndex)?.linkedParties?.findIndex(lp => lp.id === linkID) ?? -1;

        if(indexOfLink !== -1) {
          order.parties?.at(partyIndex)?.linkedParties?.splice(indexOfLink, 1);
          this.orderManager.updateOrder(order);
        }
      }));
  }

  private createCompleteRoute = (route: string, envAddress: string) => {
    return `${envAddress}/${route}`;
  }

  private generateHeaders = () => {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }
  }
}
